import { Helmet } from "react-helmet-async";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  { id: "what", title: "What Cookies Are" },
  { id: "use", title: "How We Use Them" },
  { id: "categories", title: "Categories" },
  { id: "table", title: "Cookies We Set" },
  { id: "third-parties", title: "Third Parties" },
  { id: "choices", title: "Your Choices" },
  { id: "changes", title: "Changes" },
];

const Row = ({ name, purpose, type, duration }: { name: string; purpose: string; type: string; duration: string }) => (
  <tr className="border-b border-border">
    <td className="py-2 pr-4 font-mono text-xs">{name}</td>
    <td className="py-2 pr-4 text-sm">{purpose}</td>
    <td className="py-2 pr-4 text-sm">{type}</td>
    <td className="py-2 text-sm">{duration}</td>
  </tr>
);

const CookiePolicy = () => (
  <>
    <Helmet>
      <title>Cookie Policy — The Emerging Market</title>
      <meta name="description" content="Which cookies The Emerging Market uses, why, and how to control them." />
      <link rel="canonical" href="https://emrkt.co/cookies" />
    </Helmet>
    <LegalLayout
      title="Cookie Policy"
      description="A plain explanation of the cookies and similar technologies we use on emrkt.co."
      lastUpdated="June 5, 2026"
      sections={sections}
    >
      <section id="what">
        <h2>1. What Cookies Are</h2>
        <p>
          Cookies are small text files placed on your device by a website. They let the site remember your
          actions and preferences (e.g., that you're signed in). "Similar technologies" include localStorage,
          sessionStorage, and pixels.
        </p>
      </section>

      <section id="use">
        <h2>2. How We Use Them</h2>
        <p>We use cookies to keep you signed in, remember your preferences, secure the Service against abuse, and understand how the Service is used so we can improve it.</p>
      </section>

      <section id="categories">
        <h2>3. Categories</h2>
        <ul>
          <li><strong>Strictly necessary:</strong> required for the Service to function (authentication, security, load balancing). Cannot be disabled.</li>
          <li><strong>Functional:</strong> remember your preferences (e.g., theme, dismissed banners).</li>
          <li><strong>Analytics / performance:</strong> measure usage and errors so we can improve the Service. We use these in aggregate.</li>
          <li><strong>Advertising:</strong> we do not currently use advertising or cross-site tracking cookies.</li>
        </ul>
      </section>

      <section id="table">
        <h2>4. Cookies and Storage We Set</h2>
        <div className="overflow-x-auto not-prose">
          <table className="w-full text-left border-t border-border">
            <thead>
              <tr className="border-b border-border">
                <th className="py-2 pr-4 text-xs uppercase tracking-wider text-muted-foreground">Name</th>
                <th className="py-2 pr-4 text-xs uppercase tracking-wider text-muted-foreground">Purpose</th>
                <th className="py-2 pr-4 text-xs uppercase tracking-wider text-muted-foreground">Category</th>
                <th className="py-2 text-xs uppercase tracking-wider text-muted-foreground">Duration</th>
              </tr>
            </thead>
            <tbody>
              <Row name="sb-*-auth-token" purpose="Keeps you signed in (Supabase Auth session)" type="Strictly necessary" duration="~1 year, refreshed on activity" />
              <Row name="sb-*-auth-token-code-verifier" purpose="OAuth/PKCE login flow security" type="Strictly necessary" duration="Session" />
              <Row name="emrkt-theme" purpose="Remembers your light/dark theme choice" type="Functional" duration="1 year" />
              <Row name="emrkt-onboarding" purpose="Tracks whether you've completed the welcome tour" type="Functional" duration="1 year" />
              <Row name="sentry-*" purpose="Anonymous error and performance monitoring (when enabled)" type="Analytics" duration="Session" />
            </tbody>
          </table>
        </div>
        <p className="text-xs">Exact cookie names may include unique project identifiers added by our infrastructure providers.</p>
      </section>

      <section id="third-parties">
        <h2>5. Third-Party Services</h2>
        <p>
          Some pages embed third-party content (e.g., our BetterStack public status page on <a href="/status">/status</a>). Those embeds may set their own cookies governed by their privacy policies.
        </p>
      </section>

      <section id="choices">
        <h2>6. Your Choices</h2>
        <p>
          Most browsers let you block or delete cookies in their settings. Blocking strictly necessary cookies
          will prevent you from signing in. You can also clear site data for emrkt.co at any time. If we
          introduce optional analytics or advertising cookies in the future, we will surface a consent control
          first.
        </p>
        <p className="not-prose mt-3">
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("emrkt:open-cookie-preferences"))}
            className="inline-flex items-center rounded-full border border-border px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors"
          >
            Manage cookie preferences
          </button>
        </p>
      </section>

      <section id="changes">
        <h2>7. Changes</h2>
        <p>We will update this page if our cookie use changes. See the "Last updated" date above.</p>
      </section>
    </LegalLayout>
  </>
);

export default CookiePolicy;